/**
 * VisualizationRenderer — renders AI-triggered visualizations inline in the copilot chat.
 * Maps visualization types to the appropriate Nivo chart component.
 * Supports interactive drill-down via onDrillDown callback.
 */

import { AttributionFunnel } from './visualizations/AttributionFunnel'
import type { FunnelStep } from './visualizations/AttributionFunnel'
import { PerformanceTrends } from './visualizations/PerformanceTrends'
import type { TimeSeriesDataPoint } from './visualizations/PerformanceTrends'
import { CampaignComparison } from './visualizations/CampaignComparison'
import type { CampaignComparisonData } from './visualizations/CampaignComparison'
import { BudgetSankey } from './visualizations/BudgetSankey'
import type { SankeyNode, SankeyLink } from './visualizations/BudgetSankey'
import { DemographicHeatmap } from './visualizations/DemographicHeatmap'
import type { HeatmapCell } from './visualizations/DemographicHeatmap'
import { JourneyTimeline } from './visualizations/JourneyTimeline'
import type { JourneyEvent } from './visualizations/JourneyTimeline'
import { CampaignHealthRadar } from './visualizations/CampaignHealthRadar'
import type { CampaignRadarData } from './visualizations/CampaignHealthRadar'
import { SpendRoasScatter } from './visualizations/SpendRoasScatter'
import type { ScatterPoint } from './visualizations/SpendRoasScatter'
import type { Visualization } from '@/types/copilot'

/** Event emitted when a user clicks/interacts with a visualization element */
export interface VizInteractionEvent {
  vizId: string
  vizType: string
  action: 'click'
  target: string
  label: string
  context: Record<string, unknown>
}

interface VisualizationRendererProps {
  viz: Visualization
  onDrillDown?: (event: VizInteractionEvent) => void
}

export function VisualizationRenderer({ viz, onDrillDown }: VisualizationRendererProps) {
  const { type, data, highlights } = viz

  switch (type) {
    case 'funnel':
      return (
        <AttributionFunnel
          data={(data.steps as FunnelStep[]) || []}
          highlights={highlights}
          height={280}
          onStepClick={onDrillDown ? (step: FunnelStep) => {
            onDrillDown({
              vizId: viz.id,
              vizType: 'funnel',
              action: 'click',
              target: step.id,
              label: step.label,
              context: { value: step.value, cost: step.cost },
            })
          } : undefined}
        />
      )

    case 'timeseries':
      return (
        <PerformanceTrends
          data={(data.points as TimeSeriesDataPoint[]) || []}
          metrics={(data.metrics as string[]) || ['spend']}
          highlights={highlights}
          height={250}
          yFormat={(data.yFormat as 'number' | 'currency') || 'number'}
          enableArea={(data.enableArea as boolean) ?? false}
        />
      )

    case 'comparison':
      return (
        <CampaignComparison
          data={(data.campaigns as CampaignComparisonData[]) || []}
          metrics={(data.metrics as string[]) || ['spend']}
          highlights={highlights}
          height={280}
          layout={(data.layout as 'vertical' | 'horizontal') || 'vertical'}
          valueFormat={(data.valueFormat as 'number' | 'currency' | 'percent') || 'number'}
          onBarClick={onDrillDown ? (campaign: string, metric: string, value: number) => {
            onDrillDown({
              vizId: viz.id,
              vizType: 'comparison',
              action: 'click',
              target: campaign,
              label: campaign,
              context: { metric, value },
            })
          } : undefined}
        />
      )

    case 'sankey':
      return (
        <BudgetSankey
          data={{
            nodes: (data.nodes as SankeyNode[]) || [],
            links: (data.links as SankeyLink[]) || [],
          }}
          highlights={highlights}
          height={350}
        />
      )

    case 'heatmap': {
      // Demographic breakdowns are not fetched by the insights sync, so the
      // backend sends an explicit empty payload — say so instead of faking it
      // (same treatment as the dashboard widget).
      const cells = (data.cells as HeatmapCell[]) || []
      if (data.unavailable || cells.length === 0) {
        return (
          <div
            className="flex items-center justify-center text-sm text-muted-foreground text-center px-4"
            style={{ height: 280 }}
          >
            Demographic breakdowns aren&apos;t synced yet
          </div>
        )
      }
      return (
        <DemographicHeatmap
          data={cells}
          highlights={highlights}
          height={280}
          valueLabel={(data.valueLabel as string) || 'ROAS'}
          colorScheme={(data.colorScheme as 'performance' | 'spend') || 'performance'}
        />
      )
    }

    case 'journey':
      // The copilot backend sends monetary values in whole currency units
      // (ai_engine divides cents by 100); JourneyTimeline expects cents.
      return (
        <JourneyTimeline
          events={((data.events as JourneyEvent[]) || []).map((e) => ({
            ...e,
            value: e.value != null ? Math.round(e.value * 100) : undefined,
          }))}
          height={300}
        />
      )

    case 'radar':
      return (
        <CampaignHealthRadar
          data={(data.campaigns as CampaignRadarData[]) || []}
          highlights={highlights}
          height={350}
        />
      )

    case 'scatter':
      return (
        <SpendRoasScatter
          data={(data.points as ScatterPoint[]) || []}
          highlights={highlights}
          height={320}
        />
      )

    default:
      return (
        <div className="text-xs text-muted-foreground p-2">
          Unsupported visualization type: {type}
        </div>
      )
  }
}
